/**
 * 「调整打卡任务顺序」的纯逻辑（2026-10-30，**打卡任务级**）。
 *
 * ## 用户原话（这一层存在的理由）
 *
 * > "关于目标模块下面的打卡任务，在任务多了之后，小孩界面上看就会有点乱。比如有
 * >  七八个或者 10 个左右的目标，打卡时就会不知道主次，不知道轻重缓急。……
 * >  能不能对这些打卡任务进行排序？能不能通过拖放的方式进行排序？"
 * > "你这个打卡任务调整顺序是针对目标调整，不是针对打卡任务调整。
 * >  我说的需求是针对这个打卡任务进行调整。"
 *
 * ## 排序的对象是 **habit**，不是 node
 *
 * 孩子看到的一张卡就是一个打卡任务（`habit`），而一个节点下可以挂多个任务；
 * 排节点会把它们绑在一起。所以这里列的是 `node.habits[]` 里的**每一个**，（启用中的）
 * 顺序取 `habit.sortOrder`（迁移 0021），同分时按节点顺序、创建时间兜底 ——
 * 与 `studentToday` 的 `ORDER BY` 同口径。
 */
import type { NodeSummary } from './api/types';

export interface OrderableHabit {
  /** **habit id**（排序写的就是它）。 */
  id: string;
  /** 打卡任务名（孩子端看到的任务名）。 */
  name: string;
  /** 所在节点名。 */
  nodeName: string;
  /** 上级目标名（孩子端口径的分组标题；没有就空串）。 */
  goalName: string;
  /** 当前排序值（用于稳定排序与展示）。 */
  sortOrder: number;
}

/**
 * 从节点树里取出**可排序的打卡任务**（每个 habit 一条），按当前顺序返回。
 *
 * 两条过滤（都对应"孩子端看不到的东西不该出现在排序列表里"）：
 *
 *  1. `habit.active === false` —— 已停用的任务不列；
 *  2. **祖先链上有归档节点 → 整枝不列**（2026-10-30 用户报的 bug）：
 *     > "运动这一项打卡任务，它的上级目标我已经归档了，但它为什么还在？"
 *     归档的语义是"从默认视图里拿掉"（孩子端 `visibleNodeIdsSql` 也是这么判的），
 *     所以排序列表里不该再出现它 —— 否则家长会以为它还在孩子那边出现。
 *
 * ⚠️ 只判**归档**，不判"暂停/停止"：暂停是会回来的，排序时保留它的位置才有意义。
 */
export function orderableHabitsOf(tree: readonly NodeSummary[]): OrderableHabit[] {
  const out: OrderableHabit[] = [];
  /**
   * ⚠️ **按 habit id 去重**（2026-10-31 用户报的"有重复"）。
   *
   * 输入本该是**嵌套树**；但如果谁把"已经扁平化的列表"传进来（每个节点对象
   * 仍然带着自己的 `children`），同一个节点会被走两遍 —— 于是列表里出现
   * 3 个「【语文】练字」、编号还跳号（重复的 React key 让某一行被丢掉）。
   * 这里用 id 兜住：**同一个打卡任务只出现一次**。
   */
  const seen = new Set<string>();

  const walk = (node: NodeSummary, goalName: string, ancestorArchived: boolean): void => {
    /** 祖先已归档 → 这一枝整体跳过（含它自己与所有后代）。 */
    const blocked = ancestorArchived || node.archivedAt !== null;
    if (!blocked) {
      for (const habit of node.habits ?? []) {
        if (habit.active === false) continue;
        if (seen.has(habit.id)) continue;
        seen.add(habit.id);
        out.push({
          id: habit.id,
          name: habit.name,
          nodeName: node.name,
          goalName,
          sortOrder: habit.sortOrder ?? 0,
        });
      }
    }
    /** 分组标题取**最近的目标级祖先**（孩子端的 `goalName` 就是这个口径）。 */
    const nextGoal = node.kind === 'goal' ? node.name : goalName;
    for (const child of node.children ?? []) walk(child, nextGoal, blocked);
  };

  for (const root of tree) walk(root, '', false);
  return out.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * 把"拖动后的顺序"应用到 id 列表上（纯函数，供拖放组件用）。
 *
 * @param items  当前顺序
 * @param from   被拖动项的下标
 * @param to     目标下标（已经夹到合法范围）
 * @returns 新数组（**不改入参**）；下标非法时原样返回一份拷贝
 */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return next;
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return next;
  next.splice(to, 0, moved);
  return next;
}
